import { useState, useEffect, useRef, useMemo } from "react";
import { X, Image as ImageIcon, MapPin, Plus, Trash2, Calendar, Tag, FileText, Info } from "lucide-react";
import ReactQuill from "react-quill";
import { AdminEventAPI, uploadContentImage } from "../../../services/adminEvent.api";
import Toast from "../../../components/Toast";
import ImageCropModal from "./ImageCropModal";
import { formatThousand, parseThousand } from "../../../utils/number";
import { generateEventDescription } from "../../../utils/eventDescriptionTemplate";
import { cleanQuillHtml } from "../../../utils/cleanQuillHtml";
import { dateForInput } from "../../../utils/date";
import "react-quill/dist/quill.snow.css";

/* ======================================================
   HELPERS
====================================================== */
function normalizePhoneInput(value) {
  return value.replace(/\D/g, "");
}

/* ======================================================
   CONSTANTS (DO NOT CHANGE CLASS USAGE)
====================================================== */
const EMPTY_FORM = {
  title: "",
  date: "",
  timeStart: "",
  timeEnd: "",
  locationName: "",
  locationAddress: "",
  whatsappPic: "",
  description: "",
  ticketTiers: [{ name: "Regular", price: "", quota: "" }],
  status: "draft",
  image: null,
  imagePreview: null,
};

/* ======================================================
   QUILL CONFIG (SAFE)
====================================================== */
// QUILL_MODULES is defined inside the component (useMemo) so the
// image handler can close over quillRef.

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "image",
];

/* ======================================================
   MAP PREVIEW
====================================================== */
function previewEmbedMap(input) {
  if (!input || !input.trim()) return null;
  return `https://www.google.com/maps?q=${encodeURIComponent(
    input.trim()
  )}&output=embed`;
}

export default function CreateEventModal({
  mode = "create",
  initialData,
  onClose,
  onCreated,
}) {
  const fileRef = useRef(null);
  const quillRef = useRef(null);

  const QUILL_MODULES = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
      handlers: {
        image() {
          const input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute("accept", "image/*");
          input.click();
          input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            setImgUploading(true);
            try {
              const url = await uploadContentImage(file);
              const quill = quillRef.current.getEditor();
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, "image", url);
              quill.setSelection(range.index + 1);
            } catch (err) {
              console.error("Image upload failed", err);
              setToast({ type: "error", message: "Gagal upload gambar" });
            } finally {
              setImgUploading(false);
            }
          };
        },
      },
    },
  }), []);

  const [form, setForm] = useState(EMPTY_FORM);
  const [mapPreview, setMapPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [toast, setToast] = useState(null);

  /* ======================================================
     INIT
  ====================================================== */
  useEffect(() => {
    if (mode === "edit" && initialData) {
      setForm({
        title: initialData.title || "",
        date: dateForInput(initialData.date),
        timeStart: initialData.timeStart || "",
        timeEnd: initialData.timeEnd || "",
        locationName: initialData.location?.name || "",
        locationAddress: initialData.location?.address || "",
        whatsappPic: initialData.whatsappPic || "",
        description: cleanQuillHtml(initialData.description || ""),
        ticketTiers:
          initialData.ticketTiers?.length > 0
            ? initialData.ticketTiers.map((t) => ({
                _id: t._id,
                name: t.name,
                price: formatThousand(String(t.price)),
                quota: t.quota,
              }))
            : [{ name: "Regular", price: "", quota: "" }],
        status: initialData.status || "draft",
        image: null,
        imagePreview: initialData.headerImage?.url || null,
      });

      setMapPreview(previewEmbedMap(initialData.location?.address));
    }

    if (mode === "create") {
      setForm({
        ...EMPTY_FORM,
        description: generateEventDescription({
          title: "Acara Spesial",
          location: "lokasi pilihan",
          dateText: "tanggal yang akan diumumkan",
        }),
      });
      setMapPreview(null);
    }
  }, [mode, initialData]);

  useEffect(() => {
    setMapPreview(previewEmbedMap(form.locationAddress));
  }, [form.locationAddress]);

  const update = (k, v) =>
    setForm((p) => ({ ...p, [k]: v }));

  /* ======================================================
     IMAGE
  ====================================================== */
  function handleImage(file) {
    if (!file || !file.type.startsWith("image/")) {
      return setToast({
        type: "error",
        message: "File harus berupa gambar",
      });
    }

    // Open crop modal with the raw image src
    setCropSrc(URL.createObjectURL(file));
  }

  function handleCropConfirm(croppedFile, previewUrl) {
    update("image", croppedFile);
    update("imagePreview", previewUrl);
    setCropSrc(null);
  }

  function handleCropCancel() {
    setCropSrc(null);
  }


  /* ======================================================
     TICKET TIERS
  ====================================================== */
  function addTier() {
    setForm((p) => ({
      ...p,
      ticketTiers: [...p.ticketTiers, { name: "", price: "", quota: "" }],
    }));
  }

  function updateTier(index, key, value) {
    setForm((p) => {
      const tiers = [...p.ticketTiers];
      tiers[index][key] = value;
      return { ...p, ticketTiers: tiers };
    });
  }

  function removeTier(index) {
    if (form.ticketTiers.length === 1) {
      return setToast({
        type: "error",
        message: "Minimal harus ada 1 ticket tier",
      });
    }

    setForm((p) => ({
      ...p,
      ticketTiers: p.ticketTiers.filter((_, i) => i !== index),
    }));
  }


  /* ======================================================
     SUBMIT
  ====================================================== */
  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);

      const payload = {
        title: form.title.trim(),
        date: form.date,
        timeStart: form.timeStart || "",
        timeEnd: form.timeEnd || "",
        locationName: form.locationName || "",
        locationAddress: form.locationAddress || "",
        whatsappPic: normalizePhoneInput(form.whatsappPic),
        description: cleanQuillHtml(form.description),
        status: form.status,
        ticketTiers: JSON.stringify(
          form.ticketTiers.map((t) => ({
            ...(t._id ? { _id: t._id } : {}),
            name: t.name.trim(),
            price: Number(parseThousand(t.price)),
            quota: Number(t.quota),
          }))
        ),
      };

      if (form.image) payload.image = form.image;

      const res =
        mode === "edit"
          ? await AdminEventAPI.update(initialData._id, payload)
          : await AdminEventAPI.create(payload);

      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err.response?.data?.message ||
          "Gagal menyimpan event",
      });
    } finally {
      setLoading(false);
    }
  }

  /* ======================================================
     RENDER
  ====================================================== */
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal-card ${loading ? "is-loading" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{mode === "edit" ? "Edit Event" : "Create Event"}</h2>
          <button className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          {/* IMAGE */}
          <div className="image-upload">
            <div
              className={`dropzone ${form.imagePreview ? "has-image" : ""}`}
              onClick={() => {
                fileRef.current.value = "";
                fileRef.current.click();
              }}
            >
              {form.imagePreview ? (
                <img src={form.imagePreview} alt="Event" />
              ) : (
                <div className="drop-placeholder">
                  <div className="drop-icon">
                    <ImageIcon size={22} />
                  </div>
                  <span className="drop-label">Upload Header Image</span>
                  <span className="drop-hint">1600 × 900 px · 16:9 · maks 5 MB</span>
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleImage(e.target.files[0])}
            />
          </div>

          {/* BASIC INFO */}
          <div className="form-section">
            <div className="section-title">
              <Info size={12} />
              <span>Informasi Dasar</span>
            </div>
            <div className="grid half">
              {mode === "edit" && initialData?.slug && (
                <div className="field">
                  <label>Public URL</label>
                  <div className="readonly-field mono">
                    /events/{initialData.slug}
                  </div>
                </div>
              )}
              <div className="field">
                <label>Status</label>
                <div className="status-toggle">
                  <button
                    type="button"
                    className={`status-option status-draft ${
                      form.status === "draft" ? "active" : ""
                    }`}
                    onClick={() => update("status", "draft")}
                  >
                    Draft
                  </button>
                  <button
                    type="button"
                    className={`status-option status-published ${
                      form.status === "published" ? "active" : ""
                    }`}
                    onClick={() => update("status", "published")}
                  >
                    Published
                  </button>
                </div>
              </div>
            </div>
            <div className="grid half">
              <div className="field">
                <label>Event Title</label>
                <input
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                />
              </div>
              <div className="field">
                <label>WhatsApp PIC</label>
                <input
                  value={form.whatsappPic}
                  onChange={(e) =>
                    update(
                      "whatsappPic",
                      normalizePhoneInput(e.target.value)
                    )
                  }
                  placeholder="628123456789"
                />
                <p className="helper muted">
                  Nomor WhatsApp (tanpa spasi / simbol).
                </p>
              </div>
            </div>
          </div>

          {/* SCHEDULE */}
          <div className="form-section">
            <div className="section-title">
              <Calendar size={12} />
              <span>Jadwal</span>
            </div>
            <div className="grid">
              <div className="field">
                <label>Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => update("date", e.target.value)}
                />
              </div>
              <div className="field">
                <label>Start Time</label>
                <input
                  type="time"
                  value={form.timeStart}
                  onChange={(e) => update("timeStart", e.target.value)}
                />
              </div>
              <div className="field">
                <label>End Time</label>
                <input
                  type="time"
                  value={form.timeEnd}
                  onChange={(e) => update("timeEnd", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* TICKETS */}
          <div className="form-section">
            <div className="section-title">
              <Tag size={12} />
              <span>Tiket</span>
            </div>
            {form.ticketTiers.map((t, i) => (
              <div className="tier-card" key={i}>
                <div className="grid four">
                  <div className="field">
                    <label>Ticket Name</label>
                    <input
                      value={t.name}
                      onChange={(e) =>
                        updateTier(i, "name", e.target.value)
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Price (IDR)</label>
                    <input
                      value={t.price}
                      onChange={(e) =>
                        updateTier(
                          i,
                          "price",
                          formatThousand(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Quota</label>
                    <input
                      type="number"
                      value={t.quota}
                      onChange={(e) =>
                        updateTier(i, "quota", e.target.value)
                      }
                    />
                  </div>
                  <div className="field action">
                    <button
                      type="button"
                      className="icon-btn danger"
                      onClick={() => removeTier(i)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn add-tier"
              onClick={addTier}
            >
              <Plus size={15} /> Add Ticket Tier
            </button>
          </div>

          {/* LOCATION */}
          <div className="form-section">
            <div className="section-title">
              <MapPin size={12} />
              <span>Lokasi</span>
            </div>
            <div className="grid half">
              <div className="field">
                <label>Location Name</label>
                <input
                  value={form.locationName}
                  onChange={(e) =>
                    update("locationName", e.target.value)
                  }
                />
              </div>
              <div className="field">
                <label>Google Maps URL</label>
                <input
                  value={form.locationAddress}
                  onChange={(e) =>
                    update("locationAddress", e.target.value)
                  }
                />
              </div>
            </div>
            {mapPreview && (
              <div
                className="map-preview"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <iframe src={mapPreview} loading="lazy" />
              </div>
            )}
          </div>

          {/* DESCRIPTION */}
          <div className="form-section">
            <div className="section-title">
              <FileText size={12} />
              <span>Deskripsi</span>
            </div>
            <div className="quill-wrapper">
              <ReactQuill
                ref={quillRef}
                value={form.description}
                onChange={(v) => update("description", v)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
              />
              {imgUploading && (
                <div className="quill-uploading">
                  <span className="quill-uploading__spinner" />
                  Mengupload gambar…
                </div>
              )}
            </div>
          </div>

          {/* ACTIONS */}
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Saving…" : "Save Event"}
            </button>
          </div>
        </form>

        {toast && <Toast {...toast} />}
      </div>

      {cropSrc && (
        <ImageCropModal
          src={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}
